import { useRef, useState } from 'react';
import { Download, Loader2, Paperclip, Trash2, Upload } from 'lucide-react';
import { type PlanStep, useToggleChecklistItem } from '../hooks/useNetworkPlans';
import { useDeleteEvidence, useStepEvidence, useUploadEvidence } from '@/features/stepEvidence';
import { useFeature } from '@/hooks/useCapabilities.ts';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

interface ManualStepDetailProps {
  planId: string;
  step: PlanStep;
  /** The plan is executing — checklist items are tickable and evidence is uploadable. */
  interactive: boolean;
  /** Show the evidence section (suppressed for draft plans, which have none yet). */
  showEvidence: boolean;
}

function formatBytes(n: number | null): string {
  if (n == null) return '';
  if (n < 1024) return `${String(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Checklist (with live toggle) + evidence attachments for a manual step. */
export default function ManualStepDetail({ planId, step, interactive, showEvidence }: ManualStepDetailProps) {
  const checklist = step.checklist ?? [];
  const toggleItem = useToggleChecklistItem();
  // Step evidence is SaaS-only (S3-backed); hidden + no-op'd in the trial.
  const evidenceEnabled = useFeature('stepEvidence');
  const showEvidenceSection = showEvidence && evidenceEnabled;
  const evidenceQuery = useStepEvidence(planId, step.id, showEvidenceSection);
  const uploadEvidence = useUploadEvidence();
  const deleteEvidence = useDeleteEvidence();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const hasChecklist = checklist.length > 0;
  if (!hasChecklist && !showEvidenceSection) return null;

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    uploadEvidence.mutate(
      { planId, stepId: step.id, file },
      { onError: () => { setUploadError('Upload failed. Please try again.'); } },
    );
  };

  const evidence = evidenceQuery.data ?? [];

  return (
    <div className="mt-2 space-y-2">
      {hasChecklist ? (
        <div className="space-y-1">
          {checklist.map((item) => (
            <label key={item.id} className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={item.done}
                disabled={!interactive || toggleItem.isPending}
                onCheckedChange={(checked) => {
                  toggleItem.mutate({ planId, stepId: step.id, itemId: item.id, done: checked === true });
                }}
                className="mt-0.5"
              />
              <span className={item.done ? 'line-through text-muted-foreground' : ''}>{item.text}</span>
            </label>
          ))}
        </div>
      ) : null}

      {showEvidenceSection ? (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">
              Evidence{evidence.length > 0 ? ` (${String(evidence.length)})` : ''}
            </span>
            {interactive ? (
              <>
                <input ref={fileInputRef} type="file" className="hidden" onChange={onPickFile} />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  disabled={uploadEvidence.isPending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadEvidence.isPending ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Upload className="h-3 w-3 mr-1" />
                  )}
                  Upload
                </Button>
              </>
            ) : null}
          </div>
          {uploadError ? <p className="text-xs text-sev-1">{uploadError}</p> : null}
          {evidence.map((ev) => (
            <div key={ev.id} className="flex items-center gap-2 text-xs">
              <a
                href={ev.downloadUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sev-5 hover:underline inline-flex items-center gap-1"
              >
                <Download className="h-3 w-3" />
                {ev.filename}
              </a>
              {ev.sizeBytes != null ? (
                <span className="text-muted-foreground">{formatBytes(ev.sizeBytes)}</span>
              ) : null}
              {interactive ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  disabled={deleteEvidence.isPending}
                  onClick={() => {
                    deleteEvidence.mutate({ planId, stepId: step.id, evidenceId: ev.id });
                  }}
                  aria-label={`Delete ${ev.filename}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
